import { parserOptions } from "@insite/client-framework/Common/BasicSelectors";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import { getCurrentPage } from "@insite/client-framework/Store/Data/Pages/PageSelectors";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import LocalizedDateTime from "@insite/content-library/Components/LocalizedDateTime";
import { GridContainerProps } from "@insite/mobius/GridContainer";
import Typography, { TypographyProps } from "@insite/mobius/Typography";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import constants from "@store/constants";
import { pxToRem } from "@store/Framework/Common/Utilities/unitHelper";
import LazyImage, { LazyImageProps } from "@store/MobiusOverrides/LazyImage/LazyImage";
import parse from "html-react-parser";
import * as React from "react";
import { connect } from "react-redux";
import { css } from "styled-components";

const mapStateToProps = (state: ApplicationState) => ({
    page: getCurrentPage(state),
});

type Props = WidgetProps & ReturnType<typeof mapStateToProps>;

export interface BlogPageViewStyles {
    authorText?: TypographyProps;
    publishDateText?: TypographyProps;
    newsWrapper?: GridContainerProps;
    contentWrapper?: InjectableCss;
    dateAndAuthorWrapper?: InjectableCss;
    imageWrapper?: InjectableCss;
    image?: LazyImageProps;
    pageTitle?: TypographyProps;
}

export const blogPageViewStyles: BlogPageViewStyles = {
    newsWrapper: { as: "article" },
    contentWrapper: {
        css: css`
            margin-top: ${pxToRem(8)};
        `,
    },
    authorText: {
        css: css`
            font-weight: ${x => x.theme.design.typography.weight.bold};
            margin-right: ${pxToRem(8)};
        `,
    },
    publishDateText: {
        css: css`
            font-style: italic;
        `,
    },
    pageTitle: {
        variant: "h1",
    },
};

const styles = blogPageViewStyles;

const BlogPageView = ({ page }: Props) => {
    if (!page) {
        return null;
    }

    const newsImageAlt = page.fields["newsImageAlt"] ?? page.fields["title"];

    return (
        <StyledWrapper {...styles.newsWrapper}>
            <StyledWrapper {...styles.imageWrapper}>
                <LazyImage {...styles.image} src={page.fields["newsImage"]} altText={newsImageAlt} />
            </StyledWrapper>
            <Typography {...styles.pageTitle}>{page.fields["title"]}</Typography>
            <StyledWrapper {...styles.dateAndAuthorWrapper}>
                {page.fields["author"] && <Typography {...styles.authorText}>{page.fields["author"]}</Typography>}
                {page.fields["publishDate"] && (
                    <Typography {...styles.publishDateText}>
                        <LocalizedDateTime
                            dateTime={new Date(page.fields["publishDate"])}
                            options={{ year: "numeric", month: "numeric", day: "numeric" }}
                        />
                    </Typography>
                )}
            </StyledWrapper>
            {page.fields["newsContent"] && (
                <StyledWrapper {...styles.contentWrapper}>
                    {parse(page.fields["newsContent"], parserOptions)}
                </StyledWrapper>
            )}
        </StyledWrapper>
    );
};

const widgetModule: WidgetModule = {
    component: connect(mapStateToProps)(BlogPageView),
    definition: {
        group: constants.widgetGroup.blogPage,
    },
};

export default widgetModule;
